// src/lib/rankingScore.js — THE scoring engine. One definition of "doing well",
// shared by the Desk leaderboard (IntakeTab) and the Stats tab, both fed by
// config.desk_ranking_points (Admin → General). Unified 2026-07-12 after the
// old percent-weights composite (lib/performance.js compositeScore) and the
// Desk points diverged — two boards ranking the same people differently is
// worse than either alone.
//
// Model: raw points per action. Deals dominate; the spammable actions
// (reshares, scrapes) are capped per week so volume can't outrun results;
// attendance is a small positive-only bonus — present days add points,
// lateness/absence never appears on any public surface.

export const RANK_POINT_DEFAULTS = {
  close: 150, meeting: 15,
  scrape: 5, scrape_cap: 50,
  reshare: 1, reshare_cap: 100,
  office_day: 4, perfect_week: 15,
  // Commission bonus: points per 100 units of commission earned, ON TOP of the
  // flat close points — a bigger deal scores more without one whale making the
  // flat reward meaningless. (Amounts are summed as-recorded; currencies are
  // not converted — GEL dominates and this is a motivator, not accounting.)
  commission_per_100: 1,
}

// Caps are stored as weekly numbers; scale them to the viewed window.
export function capScaleForDays(days) {
  return Math.max(days, 1) / 7
}

// Money → GEL for scoring. fx maps currency → GEL per 1 unit (config.fx_to_gel,
// Admin → General); GEL is 1 by definition. An unknown currency counts at face
// value (better under- than over-reward, and it shows up as a wrong-looking
// number instead of a silent zero).
export const FX_TO_GEL_DEFAULTS = { USD: 2.70, EUR: 3.10 }
export function toGel(amount, currency, fx) {
  const n = Number(amount) || 0
  if (!currency || currency === 'GEL') return n
  const rate = Number((fx || FX_TO_GEL_DEFAULTS)[currency] ?? FX_TO_GEL_DEFAULTS[currency])
  return n * (Number.isFinite(rate) && rate > 0 ? rate : 1)
}

// counts: { reshares, scraped, closes, meetings, officeDays, commission } —
// commission must already be in GEL (callers convert via toGel). Missing → 0.
// Returns { score, parts } — parts holds the per-metric point contributions so
// UIs can show HOW a score is composed without re-deriving the rules.
export function rankParts(counts, points, capScale, { perfect = false } = {}) {
  const P = { ...RANK_POINT_DEFAULTS, ...(points || {}) }
  const reshareCap = Math.max(1, Math.ceil(P.reshare_cap * capScale))
  const scrapeCap  = Math.max(1, Math.ceil(P.scrape_cap * capScale))
  const parts = {
    closes:   (counts.closes || 0) * P.close,
    commission: Math.floor((counts.commission || 0) / 100 * P.commission_per_100),
    meetings: (counts.meetings || 0) * P.meeting,
    scrapes:  Math.min(counts.scraped || 0, scrapeCap) * P.scrape,
    reshares: Math.min(counts.reshares || 0, reshareCap) * P.reshare,
    office:   (counts.officeDays || 0) * P.office_day + (perfect ? P.perfect_week : 0),
  }
  return { score: parts.closes + parts.commission + parts.meetings + parts.scrapes + parts.reshares + parts.office, parts }
}

// Meetings that count for points: must have an actual client (clientless rows
// are test/draft junk — found live 2026-07-12) and not have failed. Keyed by
// scheduler agent id; window on created_at, until exclusive.
//
// "Has a client" must be read from `has_client` when the row carries it (BACKLOG B61).
// The Desk now reads the server-PROJECTED meetings feed, where a colleague's client
// name/phone are nulled — so inferring "has a client" from the PII made every peer's
// meetings uncountable and their leaderboard score collapse toward 0, while StatsTab
// (raw feed, manager-only) still counted them. `has_client` is the agency-visible count
// fact the projection ships for exactly this; the PII check remains the fallback for the
// raw feed, so both callers score identically — which this engine exists to guarantee.
export function meetingsByAgent(viewings, sinceISO, untilISO) {
  const m = new Map()
  for (const v of viewings || []) {
    const at = v?.created_at
    if (!at || at < sinceISO || (untilISO && at >= untilISO)) continue
    const hasClient = v.has_client != null ? !!v.has_client : !!(v.client_name || v.client_phone)
    if (!hasClient) continue
    if (v.status === 'failed') continue
    for (const aid of v.agent_ids || []) m.set(aid, (m.get(aid) || 0) + 1)
  }
  return m
}

// Attendance: a day counts as an office day when the record carries a check-in
// and isn't absent/holiday; dedup per (agent, date) — multi-slot days are one
// day. Returns Map(agent_id → { days, hasAbsent }).
export function officeDaysByAgent(attRows) {
  const days = new Map(), absents = new Set()
  for (const r of attRows || []) {
    if (!r?.agent_id || !r?.date) continue
    if (r.status === 'absent') { absents.add(r.agent_id); continue }
    if (r.status === 'holiday') continue
    if (r.check_in || (Array.isArray(r.slots) && r.slots[0]?.check_in)) {
      if (!days.has(r.agent_id)) days.set(r.agent_id, new Set())
      days.get(r.agent_id).add(r.date)
    }
  }
  const out = new Map()
  for (const [aid, set] of days) out.set(aid, { days: set.size, hasAbsent: absents.has(aid) })
  for (const aid of absents) if (!out.has(aid)) out.set(aid, { days: 0, hasAbsent: true })
  return out
}

// ═════════════════════════════════════════════════════════════════════════════════════════
// SCORECARD v2 — owner ruling 2026-09-29 (agent ranking review). The board above (v1) paid for
// posting volume: reposts + saves were 77% of all points, and repost volume correlated 0.01 with
// meetings and 0.09 with deals across 19 agents (2026-08-03 → 09-28). The owner's three wants:
//   1. activity that CALLS OWNERS and fills our data — cooperation terms, restrictions, property
//      details ("anything filled and enriched here is good for agency");
//   2. results — meetings leading to closed deals;
//   3. consistent attendance.
// And one cost: "when agent reshares on myhome and ss they spend money — if they reshare without
// adding new cooperations that's worse than good". So reposts EARN NOTHING, and a paid repost with
// no owner work behind it (same agent, same flat) COSTS points.
//
// Inputs are the activity_ranking columns (core 2718/2719 — that migration says exactly what each
// counts), deals/commission from the /agent-deals ledger, and attendance rows.
// Every number lives in config.desk_scorecard / config.desk_floor (Admin → General); these
// objects are fallbacks only.
export const SCORE_V2_DEFAULTS = {
  coop_rich: 3,             // cooperation carrying owner-only information
  coop_default: 1,          // cooperation with only calculator defaults
  restriction: 1,           // per listing with any restrictions answer
  terms: 1,                 // per listing with terms recorded
  detail: 2,                // per property with facts corrected / valuation adjusted
  client: 10,               // client taken on AND followed up within 14 days
  meeting: 25,              // meeting with its outcome recorded
  close: 150,               // closed deal
  commission_per_100: 1,    // per 100 GEL of the agent's own commission share
  owner_cap_week: 300,      // owner-work points stop counting above this per week (scaled to the window)
  unbacked_repost: 2,       // PENALTY per paid repost with no owner work behind it
  attendance_floor: 0.5,    // multiplier at 0% attendance (1.0 at 100%)
}

// ATTENDANCE HAS ONE SOURCE OF TRUTH (owner, 2026-09-29: "we need source of truth"):
// computeAgentStats in lib/statsCompute.js — the function the Stats tab and the attendance screens
// already use. It counts each agent's SCHEDULED days from their own schedule history (not an
// assumed week), skips approved holidays, counts an explicit "absent" as missed, and treats a
// scheduled day with no record as present (the app-wide rule; it reports those days as
// unrecordedDays beside the rate). The score only reads its result, so changing that rule changes
// every screen at once. A first version of this file carried its own Mon–Sat rule and disagreed
// with the Stats tab on the same agent (100% vs ×0.50); it was removed for that reason.
// stats: a computeAgentStats result. Returns { attended, expected, unrecorded, rate } or null
// when the agent had no scheduled day in the window.
export function attendanceFromStats(stats) {
  if (!stats || !(stats.scheduledDays > 0)) return null
  return { attended: stats.presentDays, expected: stats.scheduledDays, unrecorded: stats.unrecordedDays || 0,
    rate: Math.min(1, stats.presentDays / stats.scheduledDays) }
}

// counts: an activity_ranking row (+ closes, and commission already in GEL from the deal ledger).
// Attendance multiplies the EARNED points only; the repost penalty is added after, so poor
// attendance can never shrink a penalty. Returns { score, parts, mult, unbacked }.
//
// OWNER WORK IS CAPPED (owner_cap_week × capScale, capScale = window days / 7). Measured on the
// live data the day this shipped: a busy agent records 100–370 owner-work items a WEEK (one
// logged 1,522 in a month), so uncapped it out-scored every deal on the floor by 30× and the
// board became a volume contest again — the exact disease v2 exists to cure. Up to the cap the
// work counts in full; above it, results decide the order.
export function scoreV2(counts, points, { attendance = null, capScale = 1 } = {}) {
  const P = { ...SCORE_V2_DEFAULTS, ...(points || {}) }
  const n = k => Number(counts?.[k]) || 0
  const unbacked = Math.max(0, n('paid_reposts') - n('paid_backed'))
  const parts = {
    ownerRaw: n('coop_rich') * P.coop_rich + n('coop_default') * P.coop_default
         + n('restriction_listings') * P.restriction + n('terms_listings') * P.terms
         + n('detail_properties') * P.detail,
    results: n('clients_followed') * P.client + n('meetings_done') * P.meeting
           + n('closes') * P.close + Math.floor(n('commission') / 100 * P.commission_per_100),
    reposts: -unbacked * P.unbacked_repost,
  }
  parts.owner = Math.min(parts.ownerRaw, Math.ceil((Number(P.owner_cap_week) || 0) * Math.max(capScale, 1 / 7)))
  const floor = Math.min(1, Math.max(0, Number(P.attendance_floor) || 0))
  const mult = attendance == null ? 1 : floor + (1 - floor) * attendance.rate
  const score = Math.round((parts.owner + parts.results) * mult) + parts.reposts
  return { score, parts, mult, unbacked }
}

// ── THE FLOOR — "a bottom line below which an agent becomes a candidate to leave; if they don't
// cross it in the defined timeline, they leave" (owner, 2026-09-29), and the same day: "the system
// must be evaluating dynamically, not hard-set rules, but comparing all to all" — "the weak ones
// in the group are going to leave".
//
// So there are NO fixed thresholds. Every agent's 30-day score is scaled to the days they were
// actually on the board (a 10-day agent is not compared with a 30-day total), and the line is a
// share of the OFFICE MEDIAN of that number. A line tied to the median names only agents far behind
// their colleagues; when everyone performs, nobody is below it. The optional bottom_share adds
// "and the weakest N% are at risk regardless" — off by default, because in a strong month it would
// still push someone out. All in config.desk_floor.
export const FLOOR_DEFAULTS = {
  ratio: 0.5,                   // below this share of the office median = below the line
  bottom_share: 0,              // optional: also flag the weakest share of the group (0–1); 0 = off
  window_days: 30,              // the comparison window
  min_days: 7,                  // an agent with fewer days on the board is not judged yet
  min_group: 3,                 // fewer judged agents than this → no comparison, nobody flagged
  grace_days: 14,               // time to cross back above before "leave"
  enforce_from: '2026-10-13',   // before this date the list is a TRIAL (no action)
}

const median = xs => { const v = [...xs].sort((a, b) => a - b); const n = v.length
  return n ? (n % 2 ? v[(n - 1) / 2] : (v[n / 2 - 1] + v[n / 2]) / 2) : 0 }

// rows: one per agent for ONE window — { id, score, parts: { owner, results, reposts }, mult,
// effDays } (scoreWindow in AgentScorecardPanel builds exactly this). Returns
// Map(id → { judged, norm, median, line, below, weak: [...] }) and the group numbers.
// weak names the parts where the agent trails the group: owner work or results under ratio × the
// median part, an attendance multiplier 0.1+ under the median (≈ 20 points of attendance), or a
// repost penalty worse than the median penalty by 1/ratio.
export function relativeFloor(rows, cfg) {
  const C = { ...FLOOR_DEFAULTS, ...(cfg || {}) }
  const W = Number(C.window_days) || 30
  const scaled = (r, v) => v * W / Math.max(1, Math.min(W, r.effDays || W))
  const judged = rows.filter(r => (r.effDays || 0) >= C.min_days)
  const norm = new Map(rows.map(r => [r.id, scaled(r, r.score)]))
  const group = {
    size: judged.length,
    median: median(judged.map(r => norm.get(r.id))),
    owner: median(judged.map(r => scaled(r, r.parts.owner))),
    results: median(judged.map(r => scaled(r, r.parts.results))),
    reposts: median(judged.map(r => scaled(r, r.parts.reposts))),
    silent: median(judged.map(r => scaled(r, r.parts.silent || 0))),
    mult: median(judged.map(r => r.mult)),
  }
  const line = C.ratio * group.median
  const canCompare = group.size >= C.min_group && group.median > 0
  const order = judged.map(r => r.id).sort((x, y) => norm.get(x) - norm.get(y))
  const bottomN = C.bottom_share > 0 ? Math.floor(order.length * C.bottom_share) : 0
  const bottom = new Set(order.slice(0, bottomN))
  const out = new Map()
  for (const r of rows) {
    const isJudged = (r.effDays || 0) >= C.min_days
    const n = norm.get(r.id)
    // A negative value is a DRAIN (owner: "keep agents that give us positive value rather than drain
    // us") — below the line whatever the median says.
    const below = isJudged && (n < 0 || (canCompare && (n < line || bottom.has(r.id))))
    const weak = []
    if (isJudged && canCompare) {
      if (scaled(r, r.parts.owner) < C.ratio * group.owner) weak.push('owner')
      if (scaled(r, r.parts.results) < C.ratio * group.results) weak.push('results')
      if (r.mult <= group.mult - 0.1) weak.push('attendance')   // ≈ 20 points of attendance under the median (floor 0.5)
      const pen = -scaled(r, r.parts.reposts), medPen = -group.reposts
      if (pen > 0 && pen > Math.max(medPen, 1) / C.ratio) weak.push('reposts')
      // silent scheduled days (management-only reason; never shown to the agent)
      if (r.parts.silent != null) {
        const sp = -scaled(r, r.parts.silent), medSp = -(group.silent || 0)
        if (sp > 0 && sp > Math.max(medSp, 1) / C.ratio) weak.push('silent')
      }
    }
    out.set(r.id, { judged: isJudged, norm: Math.round(n), median: Math.round(group.median), line: Math.round(line), below, weak })
  }
  return { byId: out, group: { ...group, line, canCompare } }
}

// Status from two evaluations: now, and grace_days ago. Below at both → the timeline has run out
// ('leave'); below now only → 'at_risk'. Before enforce_from every status is marked trial.
// ⚠️ Two points, not a continuous record: an agent who crossed back above in between and fell
// again reads as below throughout. The surface says so.
export function floorStatus(now, before, cfg, today = new Date()) {
  const C = { ...FLOOR_DEFAULTS, ...(cfg || {}) }
  const trial = today.toISOString().slice(0, 10) < C.enforce_from
  if (!now.below) return { status: 'ok', trial }
  if (now.below && before?.below) return { status: 'leave', trial }
  return { status: 'at_risk', trial }
}

// Managers run offices, not listings; they are off the agent board and the floor, on every surface.
export const isManagerRole = r => /manager/i.test(String(r || ''))

// The two lines drawn ON a ranking (owner, 2026-09-29: "show lines — top agents, and the bottom
// line red"; the same day: "show that in the stats tab too"). The floor's rule, applied to the
// scores being viewed: the office MEDIAN (top agents above it) and the bottom line at ratio ×
// median. rows: [{ score, effDays }] for the agents being ranked (no managers). Only agents with
// min_days on the board count toward the median. Positions use the shown score; the Floor tab's
// verdict additionally scales for days on the board and needs two windows, so a red score on a
// board is a warning, not a verdict. Returns null when there is nothing to compare.
export function boardLines(rows, floorCfg) {
  const C = { ...FLOOR_DEFAULTS, ...(floorCfg || {}) }
  const judged = rows.filter(r => (r.effDays || 0) >= C.min_days).map(r => r.score).sort((a, b) => a - b)
  if (judged.length < C.min_group) return null
  const n = judged.length
  const median = n % 2 ? judged[(n - 1) / 2] : (judged[n / 2 - 1] + judged[n / 2]) / 2
  if (!(median > 0)) return null
  return { median: Math.round(median), line: Math.round(C.ratio * median), minDays: C.min_days }
}
